/**
 * AuthShellV2 — the v2 standalone auth chrome (UI-v2 Phase 1e).
 *
 * Auth screens live OUTSIDE AppShellV2 (no sidebar/topbar). They need their
 * own v2 theme scope. This component replicates the AppShellV2 theme-scope
 * pattern on an isolated div, exactly as instructed.
 *
 * ── V2 THEME SCOPE (mirrors AppShellV2 I11 ruling) ──────────────────────────
 * `data-theme="v2"` is set on the LOCAL root div — NOT on <html>.
 * `data-v2-dark="true"` activates the dark override when the toggle fires.
 * The topbar moon/sun toggle switches v2Dark state on this root div only.
 * ThemeContext / lib/theme.ts / global <html> are UNTOUCHED.
 *
 * ── WORDMARK ─────────────────────────────────────────────────────────────────
 * The auth card is a fixed surface (light or dark per v2Dark, not global theme).
 * Logo must track the LOCAL v2Dark state, not the global effective theme.
 * Pattern: `surface={v2Dark ? "dark" : "light"}` — same as SidebarV2.
 * In light default: v2Dark=false → surface="light" → navy wordmark on pale canvas.
 * In dark mode:     v2Dark=true  → surface="dark"  → white wordmark on navy canvas.
 *
 * ── DENSITY ──────────────────────────────────────────────────────────────────
 * §2.0 ceiling enforced. Stays below the density ceiling on all padding/gap.
 * Wide variant uses max-w-lg (same as v1 AuthShell) for signup + accept-invite.
 */
import { useState, type ReactNode } from "react";
import { Wordmark } from "@/components/common/Wordmark";
import { Toast } from "@/components/common/Toast";

// ── Theme toggle icons ───────────────────────────────────────────────────────

function MoonIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      width={17}
      height={17}
      aria-hidden
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      width={17}
      height={17}
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
    </svg>
  );
}

// ── AuthShellV2 ──────────────────────────────────────────────────────────────

interface AuthShellV2Props {
  title: string;
  subtitle?: string;
  /** Footer slot rendered below the card (links, secondary CTAs). */
  footer?: ReactNode;
  children: ReactNode;
  /** Wider card variant for multi-field forms (signup, accept-invite). */
  wide?: boolean;
}

export function AuthShellV2({
  title,
  subtitle,
  footer,
  children,
  wide = false,
}: AuthShellV2Props): JSX.Element {
  // Local v2 dark state — drives `data-v2-dark` on the scope root.
  // Default = false (LIGHT). Must NOT touch global ThemeContext / <html>.
  const [v2Dark, setV2Dark] = useState(false);

  const max = wide ? "max-w-lg" : "max-w-sm";

  return (
    /*
     * v2 theme scope root — same pattern as AppShellV2.
     * data-theme="v2" activates tokens-v2.css light block.
     * data-v2-dark="true" (when v2Dark) activates the dark override.
     * Auth screens are completely outside AppShellV2, so this is the only
     * place the v2 scope gets applied on these pages.
     */
    <div
      data-theme="v2"
      {...(v2Dark ? { "data-v2-dark": "true" } : {})}
      data-testid="auth-shell-v2"
      className="relative flex min-h-screen flex-col bg-surface-canvas text-text-primary"
    >
      {/* Brand mesh — v2 tuned: blue top-left, teal bottom-right, restrained opacity */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ opacity: v2Dark ? 1 : 0.5 }}
      >
        <div
          className="absolute rounded-full"
          style={{
            left: "-10rem",
            top: "-10%",
            width: "480px",
            height: "480px",
            // Blue glow — brand lead series (--chart-1 == --accent-primary == #0077FF).
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--chart-1) 14%, transparent) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            right: "-10rem",
            bottom: "-10%",
            width: "520px",
            height: "520px",
            // Teal glow — brand accent (--accent-secondary == teal #00E5CC).
            // NB: --chart-2 was remapped to deep BLUE in tokens-v2, so the teal
            // here must reference --accent-secondary, not --chart-2.
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--accent-secondary) 10%, transparent) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* Header: wordmark left, theme toggle right */}
      <header
        className="relative z-10 flex items-center justify-between"
        style={{ height: "64px", padding: "0 24px" }}
      >
        {/*
         * Wordmark tracks LOCAL v2Dark, not global theme.
         * surface="light" → navy mark on pale canvas (light default).
         * surface="dark"  → white mark on navy canvas (dark mode).
         */}
        <Wordmark height={22} surface={v2Dark ? "dark" : "light"} />

        {/* v2 theme toggle — same icon-btn style as AppShellV2 topbar */}
        <button
          type="button"
          onClick={() => setV2Dark((v) => !v)}
          title="Toggle theme"
          aria-label="Toggle v2 theme"
          className={[
            "grid shrink-0 cursor-pointer place-items-center rounded-[var(--radius,0.5rem)]",
            "border border-transparent bg-transparent text-text-secondary",
            "transition-colors hover:bg-surface-card-hover hover:text-text-primary",
          ].join(" ")}
          style={{ width: "34px", height: "34px" }}
        >
          {v2Dark ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      {/* Main: card centered in remaining height */}
      <main
        className="relative z-10 flex flex-1 items-center justify-center"
        style={{ padding: "24px 16px" }}
      >
        <div className={`w-full ${max}`}>
          {/* Auth card: v2 surface card — rounded, 1px border, shadow-md */}
          <div
            className="rounded-2xl border border-border-default bg-surface-card shadow-md"
            style={{ padding: "16px" }}
          >
            <h1
              className="font-bold tracking-tight text-text-primary"
              style={{ fontSize: "24px", marginBottom: "4px" }}
            >
              {title}
            </h1>
            {subtitle ? (
              <p
                className="text-text-secondary"
                style={{ fontSize: "14px", marginBottom: "20px" }}
              >
                {subtitle}
              </p>
            ) : (
              <div style={{ marginBottom: "20px" }} />
            )}
            {children}
          </div>

          {footer ? (
            <p
              className="text-center text-text-secondary"
              style={{ marginTop: "16px", fontSize: "14px" }}
            >
              {footer}
            </p>
          ) : null}
        </div>
      </main>

      {/* W0-1 (SH1 / unblocks AU4): global toast host. Auth screens live outside
          AppShellV2, so without this mount every toast fired from a v2 auth flow
          (e.g. ResetPasswordV2's "password updated" confirmation) rendered
          nothing. Toast renders inline (no React portal), so mounting it inside
          this data-theme="v2" scope styles it with v2 tokens. */}
      <Toast />
    </div>
  );
}
